/**
 * Page sidebar links — custom element mirroring Dawn's MenuDrawer/HeaderDrawer.
 *
 * Uses <details>/<summary> toggle with two-phase close animation:
 *   1. Remove .menu-opening → CSS transition plays (panel slides out)
 *   2. After 400ms via requestAnimationFrame → remove [open] attribute
 *
 * This is the same pattern used by global.js MenuDrawer (lines 422-553)
 * and HeaderDrawer (lines 558-598).
 */
class PageLinksDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return;

    this.summaryElement = this.mainDetailsToggle.querySelector('summary');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    if (this.summaryElement) {
      this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));
    }

    this.querySelectorAll('[data-page-links-close]').forEach(function (btn) {
      btn.addEventListener('click', this.onCloseClick.bind(this));
    }.bind(this));

    // Backdrop click closes drawer — same as clicking outside the panel
    this.querySelectorAll('[data-page-links-backdrop]').forEach(function (el) {
      el.addEventListener('click', this.onCloseClick.bind(this));
    }.bind(this));
  }

  onSummaryClick(event) {
    var isOpen = this.mainDetailsToggle.hasAttribute('open');

    if (isOpen) {
      // Prevent native toggle — we handle close programmatically (matches Dawn)
      event.preventDefault();
      this.closeDrawer(event);
    } else {
      // Native <details> opens automatically. Add menu-opening on next frame.
      this.openDrawer();
    }
  }

  openDrawer() {
    // Add menu-opening on next frame — mirrors MenuDrawer.openMenuDrawer (global.js line 485-487)
    var self = this;
    setTimeout(function () {
      self.mainDetailsToggle.classList.add('menu-opening');
    });

    if (this.summaryElement) {
      this.summaryElement.setAttribute('aria-expanded', true);
    }

    // Body scroll lock — uses Dawn's existing overflow-hidden-tablet class (base.css line 2914)
    document.body.classList.add('overflow-hidden-tablet');
  }

  closeDrawer(event) {
    if (event === undefined) return;

    // Phase 1: Remove menu-opening → CSS transition plays
    this.mainDetailsToggle.classList.remove('menu-opening');

    // Remove body scroll lock
    document.body.classList.remove('overflow-hidden-tablet');

    if (this.summaryElement) {
      this.summaryElement.setAttribute('aria-expanded', false);
    }

    // Phase 2: Wait for animation, then remove [open]
    // Mirrors MenuDrawer.closeAnimation (global.js lines 532-553)
    this.closeAnimation(this.mainDetailsToggle);
  }

  closeAnimation(detailsElement) {
    var animationStart;

    var handleAnimation = function (time) {
      if (animationStart === undefined) {
        animationStart = time;
      }

      var elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  onCloseClick(event) {
    this.closeDrawer(event);
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.closeDrawer(event);
      if (this.summaryElement) {
        this.summaryElement.focus();
      }
    }
  }
}

customElements.define('page-links-drawer', PageLinksDrawer);
